import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { api, errorText, type DeviceDto, type FamilySummaryDto, type NotificationSettingsDto, type PairingCodeDto, type PersonDto, type StoreSectionDto } from "../api/client";
import { currentSubscription, disablePush, enablePush, pushSupport } from "../notifications/push";
import { relativeTime } from "../format";
import { useSession } from "../session";

export function SettingsPage() {
  const { me, logout } = useSession();
  return (
    <div className="page">
      <h1>Einstellungen</h1>
      <Notifications />
      {me.canManageFamily && <FamilySettings family={me.family} />}
      {me.canManageFamily && <StoreOrder />}
      <Persons canEdit={me.canManageFamily} />
      {me.canManageFamily && <Devices />}
      <section className="card account">
        <p>
          Angemeldet als <strong>{me.displayName}</strong> ({me.email}) · Rolle: {me.role === "owner" ? "Verwaltung" : me.role === "adult" ? "Erwachsen" : "Mitglied"}
        </p>
        <button className="btn" onClick={() => void logout()}>Abmelden (lokale Daten werden gelöscht)</button>
      </section>
    </div>
  );
}

/** Aufklappbarer Bereich; der Zustand wird je Gerät gemerkt. */
function Fold({ id, title, defaultOpen = false, className = "", children }: { id: string; title: string; defaultOpen?: boolean; className?: string; children: ReactNode }) {
  const key = `fp.settings.${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(key);
      return v === null ? defaultOpen : v === "1";
    } catch {
      return defaultOpen;
    }
  });
  return (
    <details className={`card fold ${className}`} open={open}
      onToggle={(e) => {
        const now = (e.currentTarget as HTMLDetailsElement).open;
        setOpen(now);
        try {
          localStorage.setItem(key, now ? "1" : "0");
        } catch {
          // nur Komfort
        }
      }}>
      <summary><h2>{title}</h2></summary>
      {children}
    </details>
  );
}

function FamilySettings({ family }: { family: FamilySummaryDto }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(family);
  const [msg, setMsg] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api<FamilySummaryDto>("/api/v1/family/settings", { method: "PUT", body: form }),
    onSuccess: async () => {
      setMsg("Gespeichert.");
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e) => setMsg(errorText(e)),
  });
  return (
    <Fold id="family" title="Familie">
      <label>Name<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
      <label>Zeitzone (IANA)<input value={form.timeZone} onChange={(e) => setForm({ ...form, timeZone: e.target.value })} /></label>
      <label className="check"><input type="checkbox" checked={form.aiEnabled} onChange={(e) => setForm({ ...form, aiEnabled: e.target.checked })} /> KI-Auswertung von Text, Sprache und Bildern (Inhalte gehen an OpenAI)</label>
      <label className="check"><input type="checkbox" checked={form.autoCommitSingleDirectEvents} onChange={(e) => setForm({ ...form, autoCommitSingleDirectEvents: e.target.checked })} /> Einzelne vollständige Termine aus eigener Texteingabe ohne Vorschau speichern</label>
      <p className="hint small">Termine aus Bildern, Dokumenten und Änderungen/Absagen benötigen immer eine Freigabe.</p>
      <button className="btn primary" onClick={() => save.mutate()} disabled={save.isPending}>Speichern</button>
      {msg && <p className="small">{msg}</p>}
    </Fold>
  );
}

function Persons({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const persons = useQuery({ queryKey: ["persons"], queryFn: () => api<PersonDto[]>("/api/v1/persons") });
  const [name, setName] = useState("");
  const [color, setColor] = useState("#c2703d");
  const [error, setError] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => api<PersonDto>("/api/v1/persons", { body: { displayName: name, color, aliases: [], isActive: true } }),
    onSuccess: async () => {
      setName("");
      await qc.invalidateQueries();
    },
    onError: (e) => setError(errorText(e)),
  });
  return (
    <Fold id="persons" title="Personen">
      <ul className="plain">
        {persons.data?.map((p) => (
          <li key={p.id}>
            <span className="swatch" style={{ background: p.color }} aria-hidden="true" /> {p.displayName}
            {p.aliases.length > 0 && <span className="muted small"> ({p.aliases.join(", ")})</span>}
            {!p.hasAccount && <span className="muted small"> · ohne eigenes Konto</span>}
            {!p.isActive && <span className="muted small"> · inaktiv</span>}
          </li>
        ))}
      </ul>
      {canEdit && (
        <div className="row">
          <input placeholder="Name (z. B. Kind ohne Konto)" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} aria-label="Farbe" />
          <button className="btn" disabled={!name.trim() || add.isPending} onClick={() => add.mutate()}>Hinzufügen</button>
        </div>
      )}
      {error && <p className="error">{error}</p>}
    </Fold>
  );
}

function Devices() {
  const qc = useQueryClient();
  const devices = useQuery({ queryKey: ["devices"], queryFn: () => api<DeviceDto[]>("/api/v1/devices") });
  const [kind, setKind] = useState<"display" | "voice_terminal">("display");
  const [deviceName, setDeviceName] = useState("Wanddisplay Küche");
  const [code, setCode] = useState<PairingCodeDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => api<PairingCodeDto>("/api/v1/devices/pairings", { body: { deviceName, kind } }),
    onSuccess: setCode,
    onError: (e) => setError(errorText(e)),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api/v1/devices/${id}/revoke`, { method: "POST", body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["devices"] }),
  });
  return (
    <Fold id="devices" title="Geräte">
      <p className="hint small">
        Ein <strong>Wanddisplay</strong> erhält nur eine bereinigte Ansicht (keine privaten Kalender, keine Notizen). Ein <strong>Sprachterminal</strong>
        (Wandtablet mit „Hey Jarvis“) arbeitet mit eigenem Gerätekonto: es darf Termine und Einkäufe eintragen, fragt „Für wen?“ und ist sofort widerrufbar.
      </p>
      <ul className="plain">
        {devices.data?.map((d) => (
          <li key={d.id} className="row between">
            <span>
              {d.name} <span className="muted small">· {d.kind === "voice_terminal" ? "Sprachterminal" : "Wanddisplay"}</span> {d.revoked ? <span className="muted small">(widerrufen)</span> : <span className="muted small">· zuletzt {d.lastSeenAt ? relativeTime(d.lastSeenAt) : "nie"}</span>}
            </span>
            {!d.revoked && <button className="btn ghost" onClick={() => revoke.mutate(d.id)}>Widerrufen</button>}
          </li>
        ))}
      </ul>
      <div className="row wrap">
        <select value={kind} aria-label="Geräteart" onChange={(e) => {
          const k = e.target.value as "display" | "voice_terminal";
          setKind(k);
          setDeviceName(k === "voice_terminal" ? "Küchen-Tablet" : "Wanddisplay Küche");
        }}>
          <option value="display">Wanddisplay</option>
          <option value="voice_terminal">Sprachterminal</option>
        </select>
        <input value={deviceName} onChange={(e) => setDeviceName(e.target.value)} maxLength={80} aria-label="Gerätename" />
        <button className="btn" onClick={() => create.mutate()} disabled={create.isPending}>Kopplungscode erzeugen</button>
      </div>
      {code && (
        <p className="pairing-code">
          Code: <strong>{code.code}</strong> – {code.kind === "voice_terminal" ? <>am Tablet auf der Anmeldeseite unter „Mit Kopplungscode anmelden“</> : <>am Display unter <code>/display</code></>} eingeben. Gültig bis {new Date(code.expiresAt).toLocaleTimeString("de-DE")}, nur einmal nutzbar.
        </p>
      )}
      {error && <p className="error">{error}</p>}
    </Fold>
  );
}

/** Laufreihenfolge im eigenen Markt (Eingang → Kasse); danach wird die Einkaufsliste sortiert. */
function StoreOrder() {
  const qc = useQueryClient();
  const sections = useQuery({ queryKey: ["shopping", "sections"], queryFn: () => api<StoreSectionDto[]>("/api/v1/shopping/sections") });
  const [order, setOrder] = useState<StoreSectionDto[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    if (sections.data) setOrder(sections.data);
  }, [sections.data]);
  const save = useMutation({
    mutationFn: (keys: string[]) => api<StoreSectionDto[]>("/api/v1/shopping/sections", { method: "PUT", body: { order: keys } }),
    onSuccess: async (saved) => {
      setOrder(saved);
      setMsg("Gespeichert – die Einkaufsliste ist jetzt so sortiert.");
      await qc.invalidateQueries({ queryKey: ["shopping"] });
    },
    onError: (e) => setMsg(errorText(e)),
  });
  const move = (index: number, delta: number) => {
    const next = [...order];
    const [item] = next.splice(index, 1);
    next.splice(index + delta, 0, item);
    setOrder(next);
    setMsg(null);
  };
  return (
    <Fold id="store" title="Einkauf: Weg durch den Markt">
      <p className="hint small">Abteilungen vom Eingang bis zur Kasse. Standard ist ein typischer REWE-Markt – passe die Reihenfolge an deinen Markt an.</p>
      <ol className="store-order">
        {order.map((s, i) => (
          <li key={s.key}>
            <span className="grow">{s.label}</span>
            <button className="btn ghost" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`${s.label} nach oben`}>↑</button>
            <button className="btn ghost" disabled={i === order.length - 1} onClick={() => move(i, 1)} aria-label={`${s.label} nach unten`}>↓</button>
          </li>
        ))}
      </ol>
      {msg && <p className="small">{msg}</p>}
      <div className="row wrap">
        <button className="btn primary" disabled={save.isPending || order.length === 0} onClick={() => save.mutate(order.map((s) => s.key))}>Reihenfolge speichern</button>
        <button className="btn" disabled={save.isPending} onClick={() => save.mutate([])}>REWE-Standard</button>
      </div>
    </Fold>
  );
}

const REMINDER_CHOICES: { value: number | null; label: string }[] = [
  { value: 10, label: "10 Minuten vorher" },
  { value: 15, label: "15 Minuten vorher" },
  { value: 30, label: "30 Minuten vorher" },
  { value: 60, label: "1 Stunde vorher" },
  { value: 120, label: "2 Stunden vorher" },
  { value: 1440, label: "1 Tag vorher" },
  { value: null, label: "Aus" },
];

function Notifications() {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ["notifications"], queryFn: () => api<NotificationSettingsDto>("/api/v1/notifications") });
  const [thisDevice, setThisDevice] = useState<string | null | undefined>(undefined);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const support = pushSupport();

  useEffect(() => {
    void currentSubscription().then((s) => setThisDevice(s?.endpoint ?? null));
  }, []);

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ["notifications"] });
    setThisDevice((await currentSubscription())?.endpoint ?? null);
  };
  const run = async (action: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      if (ok) setMsg(ok);
      await refresh();
    } catch (e) {
      setMsg(e instanceof Error && !("status" in e) ? e.message : errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const data = settings.data;
  if (!data) return null;
  const devices = data.devices ?? [];
  const active = Boolean(thisDevice);
  return (
    <Fold id="notifications" title="Mitteilungen" defaultOpen className="notifications">
      <p className="hint small">
        Erinnerung aufs Handy, wenn für dich ein Termin ansteht. Ganztägige Termine: um 7 Uhr am selben Tag.
      </p>
      {!data.available && <p className="small">Mitteilungen sind auf diesem Server ausgeschaltet.</p>}
      {data.available && !data.hasPerson && <p className="small">Dieses Konto gehört zu keiner Person – es gibt keine Terminerinnerungen.</p>}
      {data.available && data.hasPerson && (
        <>
          <label>
            Erinnern
            <select
              value={data.reminderMinutes == null ? "off" : String(data.reminderMinutes)}
              disabled={busy}
              onChange={(e) => {
                const minutes = e.target.value === "off" ? null : Number(e.target.value);
                void run(() => api("/api/v1/notifications/reminder", { method: "PUT", body: { minutes } }), "Gespeichert.");
              }}
            >
              {REMINDER_CHOICES.map((c) => (
                <option key={c.label} value={c.value == null ? "off" : String(c.value)}>{c.label}</option>
              ))}
            </select>
          </label>

          {support === "ios-needs-home-screen" && (
            <p className="small push-notice">
              Auf dem iPhone gehen Mitteilungen nur aus der App vom Home-Bildschirm: in Safari <strong>Teilen › Zum Home-Bildschirm</strong>,
              die App dort öffnen und hier aktivieren.
            </p>
          )}
          {support === "unsupported" && <p className="small">Dieser Browser kann keine Mitteilungen empfangen.</p>}
          {support === "supported" && (
            <div className="row wrap">
              {!active ? (
                <button className="btn primary" disabled={busy || !data.publicKey} onClick={() => void run(() => enablePush(data.publicKey!), "Dieses Gerät bekommt jetzt Erinnerungen.")}>
                  🔔 Auf diesem Gerät aktivieren
                </button>
              ) : (
                <>
                  <span className="badge ok">🔔 Dieses Gerät ist angemeldet</span>
                  <button className="btn" disabled={busy} onClick={() => void run(() => api("/api/v1/notifications/test", { method: "POST", body: {} }), "Probe verschickt – kommt gleich an.")}>
                    Probe senden
                  </button>
                  <button className="btn ghost" disabled={busy} onClick={() => void run(disablePush, "Auf diesem Gerät abgemeldet.")}>Abmelden</button>
                </>
              )}
            </div>
          )}

          {devices.length > 0 && (
            <ul className="plain small">
              {devices.map((d) => (
                <li key={d.id} className="row">
                  <span className="grow">{d.label ?? "Gerät"} · seit {relativeTime(d.createdAt)}</span>
                  <button className="btn ghost" disabled={busy} aria-label={`${d.label ?? "Gerät"} entfernen`}
                    onClick={() => void run(() => api("/api/v1/notifications/unsubscribe", { method: "POST", body: { deviceId: d.id } }))}>
                    Entfernen
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {msg && <p className="small">{msg}</p>}
    </Fold>
  );
}
